import React from 'react';
import { Zap, Clock, Target, TrendingUp } from 'lucide-react';

export default function PracticeIntro({ onStart }: { onStart: () => void }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="max-w-lg w-full space-y-5 animate-scale-in">
        <div className="card-base p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Zap size={24} className="text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">Adaptive Practice Session</h1>
              <p className="text-sm text-muted-foreground">8 questions · AI-weighted to your weak areas</p>
            </div>
          </div>

          <div className="p-3.5 rounded-xl bg-primary/5 border border-primary/20 mb-5">
            <p className="text-xs font-semibold text-primary uppercase tracking-wide mb-2">Today&apos;s AI Focus</p>
            <div className="space-y-1.5">
              {[
                { id: 'focus-mh', type: 'Matching Headings', weight: '5 questions', reason: 'Weakest type — 54% accuracy' },
                { id: 'focus-ynng', type: 'YN/NG', weight: '2 questions', reason: 'Spaced-repetition review due' },
                { id: 'focus-mix', type: 'Mixed types', weight: '1 question', reason: 'MCQ / TF/NG / Completion' },
              ].map((f) => (
                <div key={f.id} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                    <span className="font-medium text-foreground">{f.type}</span>
                    <span className="text-muted-foreground">· {f.reason}</span>
                  </div>
                  <span className="font-semibold text-foreground">{f.weight}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-5">
            {[
              { id: 'si-target', icon: <Target size={14} className="text-primary" />, label: 'Time target', value: '≤90s per question' },
              { id: 'si-clock', icon: <Clock size={14} className="text-warning" />, label: 'Your current avg', value: '94s — 4s over target' },
              { id: 'si-trend', icon: <TrendingUp size={14} className="text-accent" />, label: 'Session goal', value: '58%+ accuracy' },
              { id: 'si-conf', icon: <Zap size={14} className="text-info" />, label: 'Confidence rating', value: 'Rate each answer 1–5' },
            ].map((item) => (
              <div key={item.id} className="p-3 rounded-lg bg-muted border border-border">
                <div className="flex items-center gap-1.5 mb-1">
                  {item.icon}
                  <p className="text-xs text-muted-foreground">{item.label}</p>
                </div>
                <p className="text-xs font-semibold text-foreground">{item.value}</p>
              </div>
            ))}
          </div>

          <div className="space-y-2 mb-5">
            {[
              { id: 'rule-1', text: 'Answer each question before the timer reaches 90s — mark and move if unsure' },
              { id: 'rule-2', text: 'Rate your confidence after each answer — this data improves your adaptive set' },
              { id: 'rule-3', text: 'Instant feedback shows after each answer — read the distractor explanation if wrong' },
            ].map((rule) => (
              <div key={rule.id} className="flex items-start gap-2.5 text-xs text-muted-foreground">
                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground flex-shrink-0 mt-1.5" />
                {rule.text}
              </div>
            ))}
          </div>

          <button onClick={onStart} className="btn-primary w-full justify-center py-3 text-base">
            Start Drill — 8 Questions →
          </button>
        </div>
      </div>
    </div>
  );
}